/**
 * PDFDropzone — F-003.4 Phase 4 (IMPL-P4-003) + F-008 Phase 5 (IMPL-P5-001).
 *
 * Componente upload PDF con drag-drop + click-to-select + tastiera, XHR per
 * tracciare il progresso (`upload.onprogress`), validazione client-side
 * (mime + size) e stati `idle | uploading | uploaded | error`.
 *
 * Variants:
 *  - `full`     : usato in SubmitStep (wizard) — testo descrittivo + helper
 *  - `compact`  : usato in DenseSubmitFooter — single line, footer dense form
 *
 * Accessibilità:
 *  - `role="button"` + `tabIndex={0}` + `onKeyDown` (Enter/Space) → keyboard
 *  - `aria-label` sul wrapper, `aria-hidden` sulle icone decorative
 *  - `role="progressbar"` + `aria-valuenow/valuemin/valuemax` durante upload
 *  - Errori esposti tramite testo destructive accessibile (no role="alert" qui:
 *    onError callback rilancia al parent che decide come annunciarlo).
 *
 * F-008 Phase 5 — Injectable props (backward-compatible):
 *  Per evitare ~140 LOC di duplicazione tra il flusso cliente e il flusso
 *  operatore (ricevuta), il componente accetta 4 prop opzionali. Quando non
 *  fornite, il comportamento resta identico al flusso cliente F-003.4 (chiama
 *  `getPdfUploadUrl` + `confirmPdfUpload` da `pratica-client`).
 *
 *  - `getUploadUrl?` : sostituisce la chiamata di presign interna. Riceve il
 *                       `File` selezionato e ritorna `{ uploadUrl, s3Key }`.
 *  - `confirmUpload?`: chiamata DOPO la PUT XHR (al posto di `confirmPdfUpload`)
 *                       con `{ s3Key, sha256, sizeBytes }`. Lo SHA-256 è calcolato
 *                       sul `File` PRIMA della PUT con `sha256HexFromFile`.
 *  - `ariaLabel?`    : override dell'`aria-label` hard-coded sul wrapper root.
 *  - `maxSizeBytes?` : override del limite di size (default 10 MiB).
 *
 *  Quando `confirmUpload` è iniettato, il componente NON chiama `onUploaded`
 *  con un `PdfConfirmResponse` (formato pratica-client) — il caller riceve la
 *  conferma come side-effect dentro `confirmUpload` stesso. `onUploaded`
 *  viene comunque chiamato per retro-compatibilità: in modalità iniettata
 *  riceve un payload "neutro" `{ pdfS3Key: s3Key, pdfSizeBytes: sizeBytes }`
 *  che il caller può ignorare o usare per UI feedback.
 */
import { useRef, useState } from 'react';
import { Upload, FileCheck, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  getPdfUploadUrl,
  confirmPdfUpload,
  type PdfConfirmResponse,
} from '../lib/pratica-client';

/**
 * Default size limit (10 MiB). Resta esposto come fallback quando
 * `maxSizeBytes` prop non è fornito (retro-compatibilità F-003.4).
 */
const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

type DropzoneStatus = 'idle' | 'uploading' | 'uploaded' | 'error';

export interface PDFDropzoneProps {
  praticaId: string;
  onUploaded: (info: PdfConfirmResponse) => void;
  onError?: (msg: string) => void;
  variant?: 'full' | 'compact';
  /**
   * F-008 Phase 5 — Sostituisce la chiamata di presign interna. Quando
   * fornito, il componente NON chiama `getPdfUploadUrl` da pratica-client.
   * Il caller è responsabile di ottenere la presigned PUT URL (es. tramite
   * `operatorClient.presignRicevutaUpload`).
   */
  getUploadUrl?: (file: File) => Promise<{ uploadUrl: string; s3Key: string }>;
  /**
   * F-008 Phase 5 — Chiamato DOPO la PUT XHR di successo (al posto di
   * `confirmPdfUpload`). Lo SHA-256 è calcolato lato client sul `File` PRIMA
   * della PUT (Web Crypto SubtleCrypto), quindi il backend può confrontarlo
   * con l'HEAD ETag se necessario.
   */
  confirmUpload?: (args: {
    s3Key: string;
    sha256: string;
    sizeBytes: number;
  }) => Promise<void>;
  /**
   * F-008 Phase 5 — Override dell'`aria-label` sul wrapper. Default:
   * "Carica contratto PDF (max 10 MB)".
   */
  ariaLabel?: string;
  /**
   * F-008 Phase 5 — Override del limite di size in byte. Default 10 MiB.
   */
  maxSizeBytes?: number;
}

/**
 * F-008 Phase 5 — Calcola SHA-256 hex (lowercase, 64 char) di un `File`
 * usando Web Crypto API. Mirror di `sha256Hex` (lib/sha256.ts) ma per
 * binary data (File / ArrayBuffer) anziché stringa testuale.
 */
async function sha256HexFromFile(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function PDFDropzone({
  praticaId,
  onUploaded,
  onError,
  variant = 'full',
  getUploadUrl,
  confirmUpload,
  ariaLabel,
  maxSizeBytes,
}: PDFDropzoneProps) {
  const [status, setStatus] = useState<DropzoneStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // F-008 Phase 5 — limite size effettivo: prop override > default 10 MiB.
  const effectiveMaxSize = maxSizeBytes ?? MAX_SIZE;

  async function handleFile(file: File) {
    // Client-side validation: mime
    if (file.type !== 'application/pdf') {
      const msg = 'Selezionare un file PDF';
      setErrorMsg(msg);
      setStatus('error');
      onError?.(msg);
      return;
    }
    // Client-side validation: size (F-008: prop override applicato qui)
    if (file.size > effectiveMaxSize) {
      // Messaggio: il caller può fornire un limite custom, ma manteniamo il
      // testo IT idiomatico ("10 MB") per il default — coerente con i test
      // F-003.4 esistenti (no regression). In modalità iniettata con
      // `maxSizeBytes` custom, mostriamo il limite in MB.
      const limitMb = Math.round(effectiveMaxSize / (1024 * 1024));
      const msg =
        effectiveMaxSize === MAX_SIZE
          ? 'Il file supera il limite di 10 MB'
          : `Il file supera il limite di ${limitMb} MB`;
      setErrorMsg(msg);
      setStatus('error');
      onError?.(msg);
      return;
    }

    setStatus('uploading');
    setProgress(0);
    setErrorMsg('');

    try {
      // F-008 Phase 5 — Pre-compute SHA-256 sul File quando confirmUpload è
      // iniettato (l'operatore ne ha bisogno per il commit endpoint). Lo
      // calcoliamo PRIMA della PUT per garantire che corrisponda esattamente
      // al payload caricato (no race, no re-read del File dopo upload).
      const sha256 = confirmUpload ? await sha256HexFromFile(file) : null;

      // 1) richiede presigned PUT URL — prop iniettata vs default cliente.
      const presigned = getUploadUrl
        ? await getUploadUrl(file)
        : await getPdfUploadUrl(praticaId).then((r) => ({
            uploadUrl: r.uploadUrl,
            s3Key: r.key,
          }));
      const { uploadUrl, s3Key } = presigned;

      // 2) upload diretto browser → S3 via XHR (per `upload.onprogress`)
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload S3 fallito (HTTP ${xhr.status})`));
          }
        };
        xhr.onerror = () => reject(new Error('Errore di rete durante il caricamento'));
        xhr.onabort = () => reject(new Error('Caricamento annullato'));
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', 'application/pdf');
        xhr.send(file);
      });

      // 3) conferma al backend
      if (confirmUpload) {
        // F-008: modalità iniettata (operator ricevuta flow). Il caller
        // gestisce la chiamata commit (con protocolloAde, dataRegistrazione,
        // firma digitale ecc.) — qui ci limitiamo a firmare il triplo
        // canonico che l'API si aspetta.
        await confirmUpload({ s3Key, sha256: sha256!, sizeBytes: file.size });
        setStatus('uploaded');
        setProgress(100);
        // Onuploaded chiamato comunque per retro-compatibilità del contract.
        // Payload "neutro" mappato sul tipo PdfConfirmResponse del cliente.
        onUploaded({ pdfS3Key: s3Key, pdfSizeBytes: file.size });
      } else {
        // F-003.4: flusso cliente — confirmPdfUpload fa HEAD server-side.
        // Nota: l'ETag dalla risposta cross-origin non è leggibile senza CORS
        // expose-headers. In V1 lo passiamo undefined; il backend si fida di HEAD.
        const result = await confirmPdfUpload(praticaId, { sizeBytes: file.size });
        setStatus('uploaded');
        setProgress(100);
        onUploaded(result);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore durante il caricamento';
      setErrorMsg(msg);
      setStatus('error');
      onError?.(msg);
    }
  }

  const isCompact = variant === 'compact';

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel ?? 'Carica contratto PDF (max 10 MB)'}
      className={cn(
        'border-2 border-dashed rounded-lg cursor-pointer transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-primary',
        isDragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/30',
        isCompact ? 'p-3' : 'p-6',
      )}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        const f = e.dataTransfer.files[0];
        if (f) void handleFile(f);
      }}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
        }}
      />

      {status === 'idle' && (
        <div
          className={cn(
            'flex items-center gap-2',
            isCompact ? 'justify-start' : 'flex-col text-center',
          )}
        >
          <Upload className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <span className="text-sm text-muted-foreground">
            {isCompact
              ? 'Carica PDF'
              : 'Trascina qui il contratto PDF o clicca per selezionare'}
          </span>
          {!isCompact && (
            <span className="text-xs text-muted-foreground">
              Max 10 MB — solo file PDF
            </span>
          )}
        </div>
      )}

      {status === 'uploading' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            <span className="text-sm">Caricamento in corso...</span>
          </div>
          <div
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Progresso caricamento PDF"
            className="w-full bg-muted rounded-full h-2"
          >
            <div
              className="bg-primary h-2 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {status === 'uploaded' && (
        <div className="flex items-center gap-2 text-green-600">
          <FileCheck className="h-4 w-4" aria-hidden="true" />
          <span className="text-sm font-medium">PDF caricato con successo</span>
        </div>
      )}

      {status === 'error' && (
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <span className="text-sm">{errorMsg}</span>
        </div>
      )}
    </div>
  );
}
